import { ChevronDown, LogOut, User, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useAuthStore } from "@/stores/authStore";

/** The user chip from the designs, with sign-out and the way to their stokvels. */
export function UserMenu() {
  const session = useAuthStore((s) => s.session);
  const logout = useAuthStore((s) => s.logout);

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickAway = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [open]);

  if (!session) return null;

  // The role a person recognises, not a permission list.
  const roleLabel = session.roles.length
    ? session.roles.map(titleCase).join(", ")
    : "No role assigned";

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex h-touch shrink-0 items-center gap-2.5 rounded-control px-1.5 text-left hover:bg-canvas"
      >
        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-canvas" aria-hidden>
          <User className="size-5 text-ink-500" />
        </span>
        <span className="hidden min-w-0 pos:block">
          <span className="block truncate text-sm font-semibold text-ink-900">
            {session.user.displayName}
          </span>
          <span className="block truncate text-xs text-ink-500">{roleLabel}</span>
        </span>
        <ChevronDown className="size-4 shrink-0 text-ink-400" aria-hidden />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-64 rounded-card border border-line bg-surface py-1 shadow-pop"
        >
          <div className="border-b border-line px-4 py-3">
            <p className="truncate text-sm font-semibold text-ink-900">
              {session.user.displayName}
            </p>
            <p className="truncate text-xs text-ink-500">
              {session.user.email ?? `Staff code ${session.user.staffCode}`}
            </p>
            {session.merchant && (
              <p className="mt-1 truncate text-xs text-ink-400">{session.merchant.name}</p>
            )}
          </div>

          {/* The same account in the stokvel portal — one identity, two
              portals. A shop's own people only: support staff have no
              stokvels, and inside a support view this is somebody else's shop. */}
          {session.merchant && !session.user.isPlatformStaff && (
            <Link
              to="/stokvel"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-sm text-ink-900 hover:bg-canvas"
            >
              <Users className="size-4 text-ink-500" aria-hidden />
              My stokvels
            </Link>
          )}

          <button
            type="button"
            role="menuitem"
            onClick={() => void logout()}
            className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-sm text-ink-900 hover:bg-canvas"
          >
            <LogOut className="size-4 text-ink-500" aria-hidden />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function titleCase(value: string): string {
  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
